import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { Field, useField, useFormState } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import {
  InputField,
  CheckBoxField,
  SelectField,
  LocationAddressField,
} from '@unite-us/app-components';
import AddDeleteButtons from 'src/common/form/AddDeleteButtons/AddDeleteButtons';
import { locationAddressFieldNames as basicAddressFieldNames } from '@unite-us/client-utils';
import {
  defaultPhoneNumber,
  defaultEmailAddress,
  defaultAddress,
} from '../utils';

// Validation functions
const isEmail = (value) => {
  if (!value) return undefined;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(value) ? undefined : 'Invalid email address';
};

const isPhoneNumber = (value) => {
  if (!value) return undefined;
  const digits = value.replace(/\D/g, '');
  return digits.length >= 10 ? undefined : 'Invalid phone number';
};

// Shared helpers to reduce per-component complexity
const getFieldError = (meta) => (meta.touched ? meta.error : undefined);

function getVisibleCommunicationTypes(phoneType, communicationTypes) {
  const canReceiveOral = ['mobile', 'work', 'home'].includes(phoneType);
  const canReceiveWritten = phoneType === 'mobile';
  return [
    ...(canReceiveOral ? (communicationTypes.oral || []) : []),
    ...(canReceiveWritten ? (communicationTypes.written || []) : []),
  ];
}

function focusFirstButtonInContainer(containerClassName) {
  setTimeout(() => {
    const container = _.first(document.getElementsByClassName(containerClassName));
    const btnContainer = _.first(container?.getElementsByClassName('ui-add-remove-buttons'));
    const button = _.first(btnContainer?.getElementsByTagName('button'));
    if (button) {
      button.focus();
    }
  }, 200);
}

// Email row sub-component
function EmailRow({
 name, index, fieldsLength, onAdd, onRemove, communicationTypes,
}) {
  return (
    <div className="email-field">
      <div className="row">
        <div className="col-xs-9">
          <Field name={`${name}.email_address`} validate={isEmail}>
            {({ input, meta }) => (
              <InputField
                {...input}
                id={`email-${index}`}
                label="Email"
                type="email"
                inline
                error={getFieldError(meta)}
              />
            )}
          </Field>
          <div className="communication_preferences">
            <div className="communication_preferences__label-spacer" />
            <div className="communication-types-checkboxes">
              {(communicationTypes.written || []).map((type) => (
                <div
                  className="communication-types-checkboxes__checkbox-container"
                  key={`email-${index}-${type.value}`}
                >
                  <Field name={`${name}.acceptable_communication_types.${type.value}`} type="checkbox">
                    {({ input: checkInput }) => (
                      <CheckBoxField
                        className="communication-types-checkboxes__checkbox"
                        id={`email-${index}-communication-types-${type.value}-checkbox`}
                        label={type.display_name}
                        checked={checkInput.checked}
                        onChange={checkInput.onChange}
                      />
                    )}
                  </Field>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="col-xs-3">
          {index === 0 ? (
            <AddDeleteButtons
              index={index}
              displayText="Add Email"
              length={fieldsLength}
              onAddClick={onAdd}
            />
          ) : (
            <AddDeleteButtons
              index={index}
              displayText="Delete Email"
              length={fieldsLength}
              onRemoveClick={onRemove}
            />
          )}
        </div>
      </div>
    </div>
  );
}

EmailRow.propTypes = {
  name: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  fieldsLength: PropTypes.number.isRequired,
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  communicationTypes: PropTypes.object.isRequired,
};

// Phone row sub-component
function PhoneRow({
  name, index, fieldsLength, onAdd, onRemove, phoneTypes, communicationTypes, form,
}) {
  const formState = useFormState({ subscription: { values: true } });
  const phoneType = _.get(formState.values, `${name}.phone_type`, '');
  const visibleCommunicationTypes = getVisibleCommunicationTypes(phoneType, communicationTypes);

  const handlePrimaryChange = () => {
    const allPhones = _.get(formState.values, 'phone_numbers', []);
    allPhones.forEach((_phone, i) => {
      form.change(`phone_numbers[${i}].is_primary`, i === index);
    });
  };

  return (
    <div className="phone-field hide-label">
      <div className="row">
        <div className="col-xs-9">
          <div className="phone-field__wrapper">
            <Field name={`${name}.phone_number`} validate={isPhoneNumber}>
              {({ input, meta }) => (
                <InputField
                  {...input}
                  className="phone-field__digits"
                  id={`phone-${index}-number`}
                  inline
                  label="Phone Number"
                  mask="(999) 999-9999 x 99999"
                  placeholder="(999) 999-9999"
                  error={getFieldError(meta)}
                />
              )}
            </Field>
            <Field name={`${name}.phone_type`}>
              {({ input, meta }) => (
                <SelectField
                  className={`phone-${index}-type-select phone-field__phone-types`}
                  id={`phone-${index}-type`}
                  label="Phone Type"
                  labelKey="display_name"
                  valueKey="value"
                  options={phoneTypes}
                  value={input.value}
                  onChange={(val) => input.onChange(val)}
                  onBlur={input.onBlur}
                  inline
                  searchEnabled={false}
                  forceObjectValue={false}
                  error={getFieldError(meta)}
                  menuPortalTarget={document.body}
                />
              )}
            </Field>
            <Field name={`${name}.is_primary`} type="checkbox">
              {({ input: checkInput }) => (
                <CheckBoxField
                  className="phone-field__is-primary"
                  id={`phone-${index}-primary`}
                  inline
                  inputStyle={{ marginRight: '0' }}
                  label="Primary"
                  checked={checkInput.checked}
                  onChange={handlePrimaryChange}
                  style={{ fontSize: '13px' }}
                />
              )}
            </Field>
          </div>
          <div className="communication_preferences">
            <div className="communication_preferences__label-spacer" />
            <div className="communication-types-checkboxes">
              {visibleCommunicationTypes.map((type) => (
                <div
                  className="communication-types-checkboxes__checkbox-container"
                  key={`phone-${index}-${type.value}`}
                >
                  <Field name={`${name}.acceptable_communication_types.${type.value}`} type="checkbox">
                    {({ input: ctInput }) => (
                      <CheckBoxField
                        className="communication-types-checkboxes__checkbox"
                        id={`phone-${index}-communication-types-${type.value}-checkbox`}
                        label={type.display_name}
                        checked={ctInput.checked}
                        onChange={ctInput.onChange}
                      />
                    )}
                  </Field>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="col-xs-3 phone-field__add-remove">
          {index === 0 ? (
            <AddDeleteButtons
              index={index}
              displayText="Add Phone"
              length={fieldsLength}
              onAddClick={onAdd}
            />
          ) : (
            <AddDeleteButtons
              index={index}
              displayText="Delete Phone"
              length={fieldsLength}
              onRemoveClick={onRemove}
            />
          )}
        </div>
      </div>
    </div>
  );
}

PhoneRow.propTypes = {
  name: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  fieldsLength: PropTypes.number.isRequired,
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  phoneTypes: PropTypes.array.isRequired,
  communicationTypes: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
};

// Address type select + primary checkbox sub-component
function AddressTypeAndPrimary({
 name, index, addressTypes, onPrimaryChange,
}) {
  return (
    <div className="row">
      <div className="col-xs-12 col-sm-6">
        <Field name={`${name}.address_type`}>
          {({ input, meta }) => (
            <SelectField
              className="address-field-type"
              id={`address-${index}-type`}
              label="Address Type"
              labelKey="display_name"
              valueKey="value"
              options={addressTypes}
              value={input.value}
              onChange={(val) => input.onChange(val)}
              onBlur={input.onBlur}
              searchEnabled={false}
              placeholder="Select type"
              clearable
              forceObjectValue={false}
              error={getFieldError(meta)}
              menuPortalTarget={document.body}
            />
          )}
        </Field>
      </div>
      <div className="col-xs-12 col-sm-6 flex items-center">
        <Field name={`${name}.is_primary`} type="checkbox">
          {({ input: checkInput }) => (
            <CheckBoxField
              id={`address-${index}-primary`}
              inline
              label="Primary"
              checked={checkInput.checked}
              onChange={() => onPrimaryChange(index)}
              showHint={false}
              showError={false}
              className="w-auto"
            />
          )}
        </Field>
      </div>
    </div>
  );
}

AddressTypeAndPrimary.propTypes = {
  name: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  addressTypes: PropTypes.array.isRequired,
  onPrimaryChange: PropTypes.func.isRequired,
};

// Address typeahead sub-component using Google Places autocomplete
function AddressTypeahead({ name, index }) {
  const fieldNames = ['line_1', 'city', 'county', 'state', 'postal_code', 'country', 'latitude', 'longitude'];
  const fields = {};
  fieldNames.forEach((fn) => {
    fields[fn] = useField(`${name}.${fn}`);
  });

  return (
    <LocationAddressField
      id={`address-${index}`}
      label="Address Line 1"
      google={window.google}
      fieldNamePath={null}
      required={false}
      {...fields}
    />
  );
}

AddressTypeahead.propTypes = {
  name: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
};

// City, state, zip row sub-component
function AddressCityStateZip({ name, index, usStates }) {
  return (
    <div className="row">
      <div className="col-xs-12 col-sm-4">
        <Field name={`${name}.city`}>
          {({ input, meta }) => (
            <InputField
              {...input}
              autoComplete="no"
              id={`address-${index}-city`}
              label="City"
              placeholder="City"
              error={getFieldError(meta)}
            />
          )}
        </Field>
      </div>
      <div className="col-xs-12 col-sm-4">
        <Field name={`${name}.state`}>
          {({ input, meta }) => (
            <SelectField
              className="address-field-state"
              id={`address-${index}-state`}
              label="State"
              labelKey="display_name"
              valueKey="value"
              options={usStates}
              value={input.value}
              onChange={(val) => input.onChange(val)}
              onBlur={input.onBlur}
              placeholder="Select state"
              searchEnabled={false}
              forceObjectValue={false}
              error={getFieldError(meta)}
              menuPortalTarget={document.body}
            />
          )}
        </Field>
      </div>
      <div className="col-xs-12 col-sm-4">
        <Field name={`${name}.postal_code`}>
          {({ input, meta }) => (
            <InputField
              {...input}
              autoComplete="no"
              id={`address-${index}-postal-code`}
              label="Zip Code"
              maxLength={5}
              placeholder="Zip Code"
              error={getFieldError(meta)}
            />
          )}
        </Field>
      </div>
    </div>
  );
}

AddressCityStateZip.propTypes = {
  name: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  usStates: PropTypes.array.isRequired,
};

// Address row sub-component
function AddressRow({
  name, index, addressesLength, onRemove, onPrimaryChange,
  addressTypes, usStates,
}) {
  return (
    <div id={`address-${index}`} className="address-field">
      {index !== 0 && <div className="payments-insurance-client-divider" />}
      <AddressTypeAndPrimary
        name={name}
        index={index}
        addressTypes={addressTypes}
        onPrimaryChange={onPrimaryChange}
      />

      <div className="row">
        <div className="col-xs-12">
          <AddressTypeahead name={name} index={index} />
        </div>
      </div>

      <div className="row">
        <div className="col-xs-12">
          <Field name={`${name}.line_2`}>
            {({ input, meta }) => (
              <InputField
                {...input}
                autoComplete="no"
                id={`address-${index}-line2`}
                label="Address Line 2"
                error={getFieldError(meta)}
              />
            )}
          </Field>
        </div>
      </div>

      <AddressCityStateZip
        name={name}
        index={index}
        usStates={usStates}
      />

      <div className="row justify-end mx-0">
        {index !== 0 && (
          <div className="mb-4">
            <AddDeleteButtons
              index={index}
              displayText="Delete Address"
              length={addressesLength}
              onRemoveClick={onRemove}
            />
          </div>
        )}
      </div>
    </div>
  );
}

AddressRow.propTypes = {
  name: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  addressesLength: PropTypes.number.isRequired,
  onRemove: PropTypes.func.isRequired,
  onPrimaryChange: PropTypes.func.isRequired,
  addressTypes: PropTypes.array.isRequired,
  usStates: PropTypes.array.isRequired,
};

function ContactInformation(props) {
  const {
    addressTypes,
    usStates,
    phoneTypes,
    communicationTypes,
    form,
  } = props;

  const formState = useFormState({ subscription: { values: true } });

  const handleAddressPrimaryChange = (selectedIndex) => {
    const addresses = _.get(formState.values, 'addresses', []);
    addresses.forEach((_addr, i) => {
      form.change(`addresses[${i}].is_primary`, i === selectedIndex);
    });
  };

  return (
    <div className="mt-4 new-contact-information">
      <h2 className="basic-information__title">Contact Information</h2>
      <hr className="mt-1 mb-4" />

      <div className="contact-information__emails">
        <div className="contact-information__emails__label">Emails</div>
        <FieldArray name="email_addresses">
          {({ fields: emailFields }) => (
            <>
              {emailFields.map((name, index) => (
                <EmailRow
                  key={name}
                  name={name}
                  index={index}
                  fieldsLength={emailFields.length}
                  onAdd={() => emailFields.push(defaultEmailAddress())}
                  onRemove={() => {
                    emailFields.remove(index);
                    focusFirstButtonInContainer('contact-information__emails');
                  }}
                  communicationTypes={communicationTypes}
                />
              ))}
            </>
          )}
        </FieldArray>
      </div>

      <div className="contact-information__phones">
        <div className="contact-information__phones__label">Phone Numbers</div>
        <FieldArray name="phone_numbers">
          {({ fields: phoneFields }) => (
            <>
              {phoneFields.map((name, index) => (
                <PhoneRow
                  key={name}
                  name={name}
                  index={index}
                  fieldsLength={phoneFields.length}
                  onAdd={() => phoneFields.push(defaultPhoneNumber())}
                  onRemove={() => {
                    phoneFields.remove(index);
                    focusFirstButtonInContainer('contact-information__phones');
                  }}
                  phoneTypes={phoneTypes}
                  communicationTypes={communicationTypes}
                  form={form}
                />
              ))}
            </>
          )}
        </FieldArray>
      </div>

      <div className="w-full contact-information__addresses">
        <div className="contact-information__addresses__label">Addresses</div>
        <FieldArray name="addresses">
          {({ fields: addressFields }) => (
            <>
              {addressFields.map((name, index) => (
                <AddressRow
                  key={name}
                  name={name}
                  index={index}
                  addressesLength={addressFields.length}
                  onRemove={() => {
                    if (addressFields.length <= 1) {
                      addressFields.push(defaultAddress());
                    }
                    addressFields.remove(index);
                  }}
                  onPrimaryChange={handleAddressPrimaryChange}
                  addressTypes={addressTypes}
                  usStates={usStates}
                />
              ))}
              <div className="h-10 mt-1 address-form-footer">
                <AddDeleteButtons
                  displayText="Add Address"
                  onAddClick={() => {
                    addressFields.push(defaultAddress());
                    setTimeout(() => {
                      const newAddressElement = document.getElementById(`address-${addressFields.length}`);
                      const newAddressTypeSelectField = _.first(newAddressElement?.getElementsByClassName('choices'));
                      return newAddressTypeSelectField?.focus();
                    }, 200);
                  }}
                />
              </div>
            </>
          )}
        </FieldArray>
      </div>
    </div>
  );
}

ContactInformation.fields = [
  ...basicAddressFieldNames.map((name) => `addresses[].${name}`),
  'addresses[].id',
  'addresses[].line_2',
  'addresses[].address_type',
  'addresses[].is_primary',
  'email_addresses[].id',
  'email_addresses[].email_address',
  'email_addresses[].acceptable_communication_types.message',
  'email_addresses[].acceptable_communication_types.notification',
  'email_addresses[].is_primary',
  'phone_numbers[].id',
  'phone_numbers[].phone_number',
  'phone_numbers[].phone_type',
  'phone_numbers[].is_primary',
  'phone_numbers[].acceptable_communication_types.message',
  'phone_numbers[].acceptable_communication_types.notification',
  'phone_numbers[].acceptable_communication_types.phone_call',
];

ContactInformation.propTypes = {
  addressTypes: PropTypes.array.isRequired,
  usStates: PropTypes.array.isRequired,
  phoneTypes: PropTypes.array.isRequired,
  communicationTypes: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
};

ContactInformation.defaultProps = {
};

function mapStateToProps(state) {
  const contactDataEnums = _.get(state, 'session.enums.contact_data', {});
  return {
    addressTypes: _.get(contactDataEnums, 'address_types', []),
    usStates: _.get(state, 'session.enums.geography.us_states', []),
    phoneTypes: _.get(contactDataEnums, 'phone_types', []),
    communicationTypes: {
      oral: _.get(contactDataEnums, 'oral_communication_types', []),
      written: _.get(contactDataEnums, 'written_communication_types', []),
    },
  };
}

export default connect(mapStateToProps)(ContactInformation);
